import React, { useMemo, useRef, useEffect, useState, useCallback } from 'react';
import styles from '../../../../styles/CampusHierarchy.module.css';

interface HierarchyUser {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  role: string;
  employee_id: string;
  mobile_number: string;
  is_main_admin: boolean;
  designation?: string | null;
  supervisor: {
    id: string;
    full_name: string;
    designation: string;
  } | null;
  subordinates_count: number;
}

interface HierarchyLevel {
    designation: string;
    designation_display: string;
    level: number;
    is_single_account: boolean;
    users: HierarchyUser[];
}

interface TreeDiagramProps {
  hierarchyData: HierarchyLevel[];
}

interface TreeNodeData {
  id: string;
  user: HierarchyUser | null;
  isPlaceholder?: boolean;
  placeholderText?: string;
  designation?: string;
  level: number;
  children: TreeNodeData[];
  x: number;
  y: number;
  width: number;
  height: number;
}

// Single account designations that form a vertical chain (top level)
// Note: Dean is also top-level but can have multiple accounts
const SINGLE_ACCOUNT_DESIGNATIONS = [
  'chairman',
  'vice_chairman', 
  'director',
  'principal',
  'vice_principal',
  'dean'
];

// Colors for different levels
const LEVEL_COLORS: { [key: string]: string } = {
  chairman: '#6366f1',
  vice_chairman: '#8b5cf6',
  director: '#a855f7',
  principal: '#ef4444',
  vice_principal: '#f97316',
  dean: '#f59e0b',
  head_of_department: '#22c55e',
  professor: '#10b981',
  associate_professor: '#14b8a6',
  assistant_professor: '#06b6d4',
  senior_assistant: '#0ea5e9',
  junior_assistant: '#3b82f6',
  grade1_technician: '#6366f1',
  grade2_technician: '#8b5cf6',
  default: '#64748b'
};

const TreeDiagram: React.FC<TreeDiagramProps> = ({ hierarchyData }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 1200, height: 800 });
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        setDimensions({
          width: containerRef.current.offsetWidth || 1200,
          height: Math.max(containerRef.current.offsetHeight || 600, 600)
        });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, [hierarchyData]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return;
    setIsDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY });
    setPanStart({ x: panX, y: panY });
    e.preventDefault();
  }, [panX, panY]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging) return;
    setPanX(panStart.x + (e.clientX - dragStart.x));
    setPanY(panStart.y + (e.clientY - dragStart.y));
  }, [isDragging, dragStart, panStart]);

  const handleMouseUp = useCallback(() => setIsDragging(false), []);
  const handleMouseLeave = useCallback(() => setIsDragging(false), []);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.touches.length !== 1) return;
    const touch = e.touches[0];
    setIsDragging(true);
    setDragStart({ x: touch.clientX, y: touch.clientY });
    setPanStart({ x: panX, y: panY });
  }, [panX, panY]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isDragging || e.touches.length !== 1) return;
    const touch = e.touches[0];
    setPanX(panStart.x + (touch.clientX - dragStart.x));
    setPanY(panStart.y + (touch.clientY - dragStart.y));
  }, [isDragging, dragStart, panStart]);

  const handleTouchEnd = useCallback(() => setIsDragging(false), []);

  // Build the tree structure based on actual supervisor relationships from API
  // This properly reflects group memberships - if Principal joins a group, they appear under that group's lead
  const treeData = useMemo(() => {
    if (!hierarchyData || hierarchyData.length === 0) return null;

    const userMap = new Map<string, HierarchyUser>();
    
    hierarchyData.forEach(level => {
      level.users.forEach(user => {
        userMap.set(user.id, user);
      });
    });

    const allUsers = Array.from(userMap.values());
    if (allUsers.length === 0) return null;

    // Helper to get designation level (lower = higher in hierarchy)
    const getDesignationLevel = (designation: string | null | undefined): number => {
      if (!designation) return 999;
      const level = hierarchyData.findIndex(l => l.designation === designation.toLowerCase());
      return level === -1 ? 999 : level;
    };

    // CORRECT SUPERVISOR RELATIONSHIPS: Fix incorrect supervisor assignments based on designation hierarchy
    // A supervisor should have a LOWER level number (higher in hierarchy)
    // If supervisor has a HIGHER level number (lower in hierarchy), that's wrong
    allUsers.forEach(user => {
      if (user.supervisor) {
        const supervisorUser = userMap.get(user.supervisor.id);
        if (supervisorUser) {
          const userLevel = getDesignationLevel(user.designation);
          const supervisorLevel = getDesignationLevel(supervisorUser.designation);
          
          // If supervisor has higher level number (lower in hierarchy), it's incorrect
          // Example: Principal (level 3) should not have HOD (level 6) as supervisor
          if (supervisorLevel > userLevel) {
            console.log(`[TreeDiagram] Correcting supervisor for ${user.full_name} (${user.designation}, level ${userLevel}): supervisor ${supervisorUser.full_name} (${supervisorUser.designation}, level ${supervisorLevel}) is lower in hierarchy`);
            
            // Find the correct supervisor: user with designation level just above this user (one level higher)
            const correctSupervisor = allUsers.find(u => {
              const uLevel = getDesignationLevel(u.designation);
              // Find user with level just above (one level higher in hierarchy, so level number is lower)
              return uLevel === userLevel - 1 && u.id !== user.id;
            });
            
            if (correctSupervisor) {
              console.log(`[TreeDiagram] Setting correct supervisor for ${user.full_name}: ${correctSupervisor.full_name} (${correctSupervisor.designation})`);
              user.supervisor = {
                id: correctSupervisor.id,
                full_name: correctSupervisor.full_name,
                designation: correctSupervisor.designation || ''
              };
            } else {
              // If no direct supervisor found, try to find any user with lower level number
              const higherLevelSupervisor = allUsers
                .filter(u => {
                  const uLevel = getDesignationLevel(u.designation);
                  return uLevel < userLevel && u.id !== user.id;
                })
                .sort((a, b) => getDesignationLevel(a.designation) - getDesignationLevel(b.designation))
                .pop(); // Get the one closest in level (highest level number that's still lower)
              
              if (higherLevelSupervisor) {
                console.log(`[TreeDiagram] Setting closest supervisor for ${user.full_name}: ${higherLevelSupervisor.full_name} (${higherLevelSupervisor.designation})`);
                user.supervisor = {
                  id: higherLevelSupervisor.id,
                  full_name: higherLevelSupervisor.full_name,
                  designation: higherLevelSupervisor.designation || ''
                };
              } else {
                // If no supervisor found, set to null (will be handled as root or connected later)
                console.log(`[TreeDiagram] No correct supervisor found for ${user.full_name}, setting to null`);
                user.supervisor = null;
              }
            }
          }
        }
      }
    });

    // Find root users (no supervisor)
    let rootUsers = allUsers.filter(u => !u.supervisor);
    
    // CYCLE DETECTION: If no roots exist, everyone has a supervisor -> there's a cycle
    // Break the cycle by finding the user with highest designation level (smallest level number)
    if (rootUsers.length === 0 && allUsers.length > 0) {
      console.log('[TreeDiagram] No root users found - detecting cycle');
      
      // Find the topmost user by designation hierarchy
      const sortedByDesignation = [...allUsers].sort((a, b) => {
        return getDesignationLevel(a.designation) - getDesignationLevel(b.designation);
      });
      
      const topmostUser = sortedByDesignation[0];
      console.log('[TreeDiagram] Breaking cycle at topmost user:', topmostUser.full_name, topmostUser.designation);
      
      // Make this user a root by treating them as having no supervisor for tree building
      rootUsers = [topmostUser];
    }
    
    // Sort root users by designation level (top-level first)
    rootUsers.sort((a, b) => {
      const aLevel = getDesignationLevel(a.designation);
      const bLevel = getDesignationLevel(b.designation);
      if (aLevel !== bLevel) return aLevel - bLevel;
      return a.full_name.localeCompare(b.full_name);
    });
    
    // Track visited users to prevent infinite loops in cycle scenarios
    const visited = new Set<string>();

    // Build tree node recursively following supervisor relationships
    const buildUserNode = (user: HierarchyUser, depth: number): TreeNodeData => {
      // Mark as visited to prevent cycles
      visited.add(user.id);
      
      // Find all users whose supervisor is this user (and not already visited)
      let children = allUsers.filter(u => 
        u.supervisor?.id === user.id && !visited.has(u.id)
      );
      
      // Also find users that should be under this user based on designation hierarchy
      // but aren't connected yet (their supervisor might be wrong or they're disconnected)
      const userLevel = getDesignationLevel(user.designation);
      const potentialChildren = allUsers.filter(u => {
        if (visited.has(u.id)) return false;
        if (u.supervisor?.id === user.id) return false; // Already a child
        
        const uLevel = getDesignationLevel(u.designation);
        
        // If this user is one level above the potential child, and the child's supervisor
        // is wrong (higher level number) or missing, connect them
        if (uLevel === userLevel + 1) {
          if (!u.supervisor) return true; // No supervisor, should be under this user
          
          const supervisorLevel = getDesignationLevel(u.supervisor.designation);
          // If supervisor is wrong (lower in hierarchy), this user should be the supervisor
          if (supervisorLevel > userLevel) return true;
        }
        
        return false;
      });
      
      // Add potential children and update their supervisor relationship
      potentialChildren.forEach(child => {
        child.supervisor = {
          id: user.id,
          full_name: user.full_name,
          designation: user.designation || ''
        };
      });
      
      children = [...children, ...potentialChildren];
      
      // Sort children: single-account designations first (for vertical chain), then others
      children.sort((a, b) => {
        const aIsSingle = SINGLE_ACCOUNT_DESIGNATIONS.includes(a.designation?.toLowerCase() || '');
        const bIsSingle = SINGLE_ACCOUNT_DESIGNATIONS.includes(b.designation?.toLowerCase() || '');
        
        // Single-account comes first
        if (aIsSingle && !bIsSingle) return -1;
        if (!aIsSingle && bIsSingle) return 1;
        
        // Then sort by designation level
        const aLevel = getDesignationLevel(a.designation);
        const bLevel = getDesignationLevel(b.designation);
        if (aLevel !== bLevel) return aLevel - bLevel;
        
        return a.full_name.localeCompare(b.full_name);
      });

      return {
        id: user.id,
        user,
        level: depth,
        children: children.map(child => buildUserNode(child, depth + 1)),
        x: 0,
        y: 0,
        width: 220,
        height: 150
      };
    };

    // Build initial tree
    let tree: TreeNodeData | null = null;
    if (rootUsers.length === 1) {
      tree = buildUserNode(rootUsers[0], 0);
    } else if (rootUsers.length > 1) {
      // Multiple roots - create a virtual root to hold them
      const chainedRoots: TreeNodeData[] = [];
      
      // Build each root's subtree
      rootUsers.forEach(rootUser => {
        chainedRoots.push(buildUserNode(rootUser, 0));
      });

      tree = {
        id: 'virtual-root',
        user: null,
        isPlaceholder: true,
        placeholderText: 'Organization',
        level: -1,
        children: chainedRoots,
        x: 0,
        y: 0,
        width: 120,
        height: 80
      };
    }

    // Connect any remaining unvisited users to the tree based on designation hierarchy
    const unvisitedUsers = allUsers.filter(u => !visited.has(u.id));
    if (unvisitedUsers.length > 0 && tree) {
      console.log(`[TreeDiagram] Found ${unvisitedUsers.length} unvisited users, connecting them to tree`);
      
      // Helper to find the correct parent for a user in the tree
      const findParentForUser = (targetUser: HierarchyUser, node: TreeNodeData): TreeNodeData | null => {
        const targetLevel = getDesignationLevel(targetUser.designation);
        const nodeLevel = node.user ? getDesignationLevel(node.user.designation) : 999;
        
        // If this node is one level above the target user, it's the parent
        if (nodeLevel === targetLevel - 1) {
          return node;
        }
        
        // Check children
        for (const child of node.children) {
          const parent = findParentForUser(targetUser, child);
          if (parent) return parent;
        }
        
        return null;
      };
      
      // Connect each unvisited user
      unvisitedUsers.forEach(unvisitedUser => {
        if (!tree) return;
        
        const parentNode = findParentForUser(unvisitedUser, tree);
        if (parentNode && parentNode.user) {
          console.log(`[TreeDiagram] Connecting ${unvisitedUser.full_name} under ${parentNode.user.full_name}`);
          // Update supervisor relationship
          unvisitedUser.supervisor = {
            id: parentNode.user.id,
            full_name: parentNode.user.full_name,
            designation: parentNode.user.designation || ''
          };
          // Add as child to parent node
          parentNode.children.push(buildUserNode(unvisitedUser, parentNode.level + 1));
        } else {
          // If no parent found, add as a new root (shouldn't happen, but handle it)
          console.log(`[TreeDiagram] No parent found for ${unvisitedUser.full_name}, adding as root`);
          if (!tree.isPlaceholder) {
            // Convert single root to virtual root
            const oldTree: TreeNodeData = tree;
            tree = {
              id: 'virtual-root',
              user: null,
              isPlaceholder: true,
              placeholderText: 'Organization',
              level: -1,
              children: [oldTree, buildUserNode(unvisitedUser, 0)],
              x: 0,
              y: 0,
              width: 120,
              height: 80
            };
          } else {
            tree.children.push(buildUserNode(unvisitedUser, 0));
          }
        }
      });
    }

    return tree;
  }, [hierarchyData]);

  // Position nodes in tree layout
  const positionedTree = useMemo(() => {
    if (!treeData) return null;

    const NODE_WIDTH = 220;
    const NODE_HEIGHT = 150;
    const H_SPACING = 50;
    const V_SPACING = 80;
    const LOGO_HEIGHT = 120;

    // Check if a node is single-account designation
    const isSingleAccountNode = (node: TreeNodeData): boolean => {
      if (!node.user?.designation) return false;
      return SINGLE_ACCOUNT_DESIGNATIONS.includes(node.user.designation.toLowerCase());
    };

    // Calculate width needed for a subtree
    const calculateWidth = (node: TreeNodeData): number => {
      if (node.isPlaceholder) {
        // Virtual root - sum of all children widths
        if (node.children.length === 0) return NODE_WIDTH;
        const childrenWidth = node.children.reduce((sum, child) => sum + calculateWidth(child), 0);
        const spacing = (node.children.length - 1) * H_SPACING;
        return childrenWidth + spacing;
      }

      if (node.children.length === 0) {
        return NODE_WIDTH;
      }

      // Separate single-account child (vertical) from others (horizontal)
      const verticalChild = node.children.find(c => isSingleAccountNode(c));
      const horizontalChildren = node.children.filter(c => !isSingleAccountNode(c));
      
      if (horizontalChildren.length === 0 && verticalChild) {
        // Only vertical chain
        return Math.max(NODE_WIDTH, calculateWidth(verticalChild));
      }
      
      // Calculate horizontal children width
      const childrenWidth = horizontalChildren.reduce((sum, child) => sum + calculateWidth(child), 0);
      const spacing = horizontalChildren.length > 1 ? (horizontalChildren.length - 1) * H_SPACING : 0;
      let totalWidth = childrenWidth + spacing;
      
      // If there's a vertical child, consider its width too
      if (verticalChild) {
        totalWidth = Math.max(totalWidth, calculateWidth(verticalChild));
      }
      
      return Math.max(NODE_WIDTH, totalWidth);
    };

    // Position a node and its children
    const positionNode = (node: TreeNodeData, x: number, y: number, centerX: number): TreeNodeData => {
      // Handle virtual root (multiple roots)
      if (node.isPlaceholder) {
        const positioned = { ...node, x: centerX - 60, y: y };
        
        if (node.children.length === 0) return positioned;
        
        const totalWidth = calculateWidth(node);
        let currentX = centerX - totalWidth / 2;
        const childY = y + 100; // Small gap after virtual root
        
        positioned.children = node.children.map(child => {
          const childWidth = calculateWidth(child);
          const childCenterX = currentX + childWidth / 2;
          const childX = childCenterX - NODE_WIDTH / 2;
          currentX += childWidth + H_SPACING;
          return positionNode(child, childX, childY, childCenterX);
        });
        
        return positioned;
      }

      const positioned = { ...node, x, y };

      if (node.children.length === 0) {
        return positioned;
      }

      // Separate single-account child (vertical) from others (horizontal)
      const verticalChild = node.children.find(c => isSingleAccountNode(c));
      const horizontalChildren = node.children.filter(c => !isSingleAccountNode(c));
      
      const positionedChildren: TreeNodeData[] = [];
      let nextY = y + NODE_HEIGHT + V_SPACING;

      // Position vertical child (next in single-account chain) - centered below parent
      if (verticalChild) {
        const verticalChildX = centerX - NODE_WIDTH / 2;
        const positionedVertical = positionNode(verticalChild, verticalChildX, nextY, centerX);
        positionedChildren.push(positionedVertical);
        
        // Get the bottom-most Y position from the vertical chain for horizontal children
        const getMaxY = (n: TreeNodeData): number => {
          if (n.children.length === 0) return n.y;
          return Math.max(n.y, ...n.children.map(getMaxY));
        };
        nextY = getMaxY(positionedVertical) + NODE_HEIGHT + V_SPACING;
      }

      // Position horizontal children - spread out below
      if (horizontalChildren.length > 0) {
        const childrenTotalWidth = horizontalChildren.reduce((sum, child) => sum + calculateWidth(child), 0);
        const childrenSpacing = horizontalChildren.length > 1 ? (horizontalChildren.length - 1) * H_SPACING : 0;
        const totalWidth = childrenTotalWidth + childrenSpacing;
        
        const horizontalY = verticalChild ? nextY : y + NODE_HEIGHT + V_SPACING;
        let currentX = centerX - totalWidth / 2;

        horizontalChildren.forEach(child => {
          const childWidth = calculateWidth(child);
          const childCenterX = currentX + childWidth / 2;
          const childX = childCenterX - NODE_WIDTH / 2;
          positionedChildren.push(positionNode(child, childX, horizontalY, childCenterX));
          currentX += childWidth + H_SPACING;
        });
      }

      positioned.children = positionedChildren;
      return positioned;
    };

    const treeWidth = calculateWidth(treeData);
    const centerX = dimensions.width / 2;
    const startX = treeData.isPlaceholder ? centerX - 60 : centerX - NODE_WIDTH / 2;
    const startY = LOGO_HEIGHT + 40;

    return positionNode(treeData, startX, startY, centerX);
  }, [treeData, dimensions.width]);

  // Calculate SVG bounds
  const bounds = useMemo(() => {
    if (!positionedTree) return { minX: 0, maxX: 1200, minY: 0, maxY: 800 };

    const collectBounds = (node: TreeNodeData, acc: { minX: number; maxX: number; minY: number; maxY: number }) => {
      acc.minX = Math.min(acc.minX, node.x);
      acc.maxX = Math.max(acc.maxX, node.x + node.width);
      acc.minY = Math.min(acc.minY, node.y);
      acc.maxY = Math.max(acc.maxY, node.y + node.height);
      node.children.forEach(child => collectBounds(child, acc));
      return acc;
    };

    return collectBounds(positionedTree, { 
      minX: Infinity, 
      maxX: -Infinity, 
      minY: Infinity, 
      maxY: -Infinity 
    });
  }, [positionedTree]);

  const getColor = (designation: string | null | undefined): string => {
    if (!designation) return LEVEL_COLORS.default;
    return LEVEL_COLORS[designation.toLowerCase()] || LEVEL_COLORS.default;
  };

  const formatDesignation = (designation: string | null | undefined): string => {
    if (!designation) return 'No Designation';
    return designation.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Render curved connector with proper styling
  const renderConnector = (parent: TreeNodeData, child: TreeNodeData, key: string) => {
    const startX = parent.x + parent.width / 2;
    const startY = parent.y + parent.height;
    const endX = child.x + child.width / 2;
    const endY = child.y;
    
    const midY = startY + (endY - startY) / 2;
    
    const path = `M ${startX} ${startY} C ${startX} ${midY}, ${endX} ${midY}, ${endX} ${endY}`;

    return (
      <g key={key}>
        
        {/* Main connector line */}
        <path
          d={path}
          fill="none"
          stroke="#94a3b8"
          strokeWidth={2}
          strokeLinecap="round"
        />
        {/* Dot at end */}
        <circle
          cx={endX}
          cy={endY - 5}
          r={5}
          fill="#94a3b8"
        />
      </g>
    );
  };

  const renderConnectors = (node: TreeNodeData): React.ReactElement[] => {
    const connectors: React.ReactElement[] = [];
    
    node.children.forEach((child, index) => {
      if (!node.isPlaceholder) {
        connectors.push(renderConnector(node, child, `conn-${node.id}-${child.id}-${index}`));
      }
      connectors.push(...renderConnectors(child));
    });

    return connectors;
  };

  // Render organization logo inside SVG
  const renderLogo = (centerX: number) => {
    const logoSize = 80;
    const logoY = 20;
    
    return (
      <g>
        {/* Logo background circle with gradient */}
        <defs>
          <filter id="logoShadow" x="-50%" y="-50%" width="200%" height="200%">
            <feDropShadow dx="0" dy="4" stdDeviation="8" floodOpacity="0.15" />
          </filter>
        </defs>

        {/* Main circle */}
        <circle
          cx={centerX}
          cy={logoY + logoSize / 2}
          r={logoSize / 2}
          fill="white"
          stroke="#6366f1"
          strokeWidth="1"
          filter="url(#logoShadow)"
        />
        
        {/* Logo image */}
        <foreignObject 
          x={centerX - logoSize / 2 + 12} 
          y={logoY + 12} 
          width={logoSize - 24} 
          height={logoSize - 24}
        >
          <div style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'white',
            borderRadius: '50%'
          }}>
            <img 
              src="/images/wallet-transparent.png" 
              alt="Organization" 
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain'
              }}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
              }}
            />
          </div>
        </foreignObject>
      </g>
    );
  };

  // Render a single node card
  const renderNode = (node: TreeNodeData): React.ReactElement => {
    if (node.isPlaceholder) {
      return <g key={node.id}>{node.children.map(child => renderNode(child))}</g>;
    }

    const user = node.user!;
    const color = getColor(user.designation);

    return (
      <g key={node.id}>
        <foreignObject
          x={node.x}
          y={node.y}
          width={node.width}
          height={node.height}
        >
          <div className={styles.orgCard}>
            {/* Color accent bar */}
            <div className={styles.orgCardAccent} style={{ backgroundColor: color }} />
            
            <div className={styles.orgCardBody}>
              {/* Header with avatar */}
              <div className={styles.orgCardHeader}>
                <div className={styles.orgAvatar} style={{ backgroundColor: color }}>
                  {user.full_name.charAt(0).toUpperCase()}
                </div>
                <div className={styles.orgHeaderInfo}>
                  <h4 className={styles.orgName}>{user.full_name}</h4>
                  {user.is_main_admin && (
                    <span className={styles.orgAdminBadge}>Admin</span>
                  )}
                </div>
              </div>
              
              {/* Designation badge */}
              <div className={styles.orgDesignation} style={{ backgroundColor: `${color}15`, color: color }}>
                {formatDesignation(user.designation)}
              </div>
              
              {/* Contact info */}
              <div className={styles.orgContact}>
                <div className={styles.orgContactItem}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="2" y="4" width="20" height="16" rx="2" />
                    <path d="m22,7-10,6L2,7" />
                  </svg>
                  <span title={user.email}>
                    {user.email.length > 18 ? user.email.substring(0, 15) + '...' : user.email}
                  </span>
                </div>
                {user.mobile_number && (
                  <div className={styles.orgContactItem}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.362 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.574 2.81.7A2 2 0 0 1 22 16.92z" />
                    </svg>
                    <span>{user.mobile_number}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </foreignObject>

        {node.children.map(child => renderNode(child))}
      </g>
    );
  };

  const resetView = () => {
    setZoomLevel(1);
    setPanX(0);
    setPanY(0);
  };

  if (!positionedTree || (positionedTree.isPlaceholder && positionedTree.children.length === 0)) {
    return (
      <div className={styles.emptyTree}>
        <div className={styles.emptyIcon}>
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
        </div>
        <h3>No Hierarchy Data</h3>
        <p>Add users to the system to see the organizational structure.</p>
      </div>
    );
  }

  // Calculate SVG dimensions - center the tree
  const treeWidth = bounds.maxX - bounds.minX;
  const treeHeight = bounds.maxY + 100;
  const svgWidth = Math.max(treeWidth + 300, dimensions.width);
  const svgHeight = Math.max(treeHeight, dimensions.height);
  
  // Center the viewBox around the tree content
  const viewBoxX = bounds.minX - (svgWidth - treeWidth) / 2;

  // Calculate logo center position - always centered above the tree
  const logoCenterX = (bounds.minX + bounds.maxX) / 2;

  return (
    <div ref={containerRef} className={styles.treeContainer}>
      <svg
        width="100%"
        height="100%"
        viewBox={`${viewBoxX} 0 ${svgWidth} ${svgHeight}`}
        className={styles.treeSvg}
        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        preserveAspectRatio="xMidYMin meet"
      >
          <defs>
          <linearGradient id="connectorGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#6366f1" />
                    <stop offset="100%" stopColor="#8b5cf6" />
                  </linearGradient>
          <pattern id="dotPattern" width="30" height="30" patternUnits="userSpaceOnUse">
            <circle cx="2" cy="2" r="1" fill="rgba(99, 102, 241, 0.06)" />
          </pattern>
                </defs>

        <g transform={`translate(${panX / zoomLevel}, ${panY / zoomLevel}) scale(${zoomLevel})`}>
          {/* Background pattern */}
          <rect x={viewBoxX - 500} y={-100} width={svgWidth + 1000} height={svgHeight + 200} fill="url(#dotPattern)" />

          {/* Organization Logo - now inside SVG, moves with pan/zoom */}
          {renderLogo(logoCenterX)}

          {/* Connection from logo to first node */}
          {positionedTree && !positionedTree.isPlaceholder && (
            <path
              d={`M ${logoCenterX} 100 
                  Q ${logoCenterX} ${(100 + positionedTree.y) / 2}, ${positionedTree.x + positionedTree.width / 2} ${positionedTree.y}`}
              fill="none"
              stroke="url(#connectorGradient)"
              strokeWidth={3}
              strokeDasharray="8,4"
              opacity={0.6}
            />
          )}

          {/* Render connectors */}
          <g className={styles.connections}>
            {renderConnectors(positionedTree)}
          </g>

          {/* Render nodes */}
          <g className={styles.nodes}>
            {renderNode(positionedTree)}
        </g>
        </g>
      </svg>

      {/* Zoom Controls */}
      <div className={styles.zoomControls}>
        <button
          className={styles.zoomButton}
          onClick={() => setZoomLevel(prev => Math.min(prev + 0.2, 2.5))}
          title="Zoom In"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
            <line x1="11" y1="8" x2="11" y2="14" />
            <line x1="8" y1="11" x2="14" y2="11" />
          </svg>
        </button>
        <button
          className={styles.zoomButton}
          onClick={() => setZoomLevel(prev => Math.max(prev - 0.2, 0.3))}
          title="Zoom Out"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
            <line x1="8" y1="11" x2="14" y2="11" />
          </svg>
        </button>
        <button className={styles.zoomButton} onClick={resetView} title="Reset View">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
            <path d="M21 3v5h-5" />
            <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
            <path d="M3 21v-5h5" />
          </svg>
        </button>
        <span className={styles.zoomLevel}>{Math.round(zoomLevel * 100)}%</span>
      </div>
    </div>
  );
};

export default TreeDiagram;
